import { Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import type { HtmlAttributes } from '@studio/design-system/props';
import { AudioDefaults } from '@studio/models/audio';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import type { ReactElement, SyntheticEvent } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { Offset } from '#~/audio/components/offset/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

/** `<Settings>` component. */
export const Settings = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const store = useStore(
    ({ candidate, previews }) => ({
      name: candidate.name,
      offset: candidate.audio?.offset ?? AudioDefaults.offset,
      videoId: previews.video?.deviceId
    }),
    shallow
  );

  const { track } = useAnalytics();

  const handleNameChange = useLatestFn((nextValue: string, event: SyntheticEvent) => {
    const nextName = toString(nextValue);

    if (nextName && nextName !== store.name) {
      producer((draft) => {
        draft.candidate.name = nextName;
      });
    } else {
      event.preventDefault();
    }
  });

  const handleOffsetChange = useLatestFn((nextOffset: number) => {
    producer((draft) => {
      const { audio } = draft.candidate;

      draft.candidate.audio = { ...audio, offset: nextOffset };
      // TODO clean: not sure if this is the best place to put this event; better in audio/commands/update.ts?
      track(EVENT_NAMES.SET_AUDIO_DELAY, EVENT_CATEGORIES.AUDIO_SOURCES, {
        offset: nextOffset,
        candidate: draft.candidate,
        studioId: draft.roomId?.toString()
      });
    });
  });

  return (
    <Column {...props}>
      <Group label='Audio source'>
        <Mediator disabled={!!store.videoId} onCommit={handleNameChange} value={store.name} />
      </Group>

      <Offset offset={toAccessor(store.offset, handleOffsetChange)} />
    </Column>
  );
};
